import React from 'react'
import './Illustration.scss'
import { configs } from '@configs'
import { withBase } from '@utils/basePath'

export interface IllustrationProps {
  /** Defaults to app default theme brand when omitted (e.g. context not mounted). */
  brand?: string
  name: string
  width?: number | string
  className?: string
  alt?: string
}

// Common illustration names that might be used
export const ILLUSTRATION_NAMES = {
  // SI Brand illustrations
  'waitlist-matches-found': 'Waitlist Matches Found',
  'registration-activation': 'Registration Activation',
  'deposit-confirmation': 'Deposit Confirmation',
  'down-for-maintenance': 'Down For Maintenance',
  'location-tracking': 'Location Tracking',
  'recover-username': 'Recover Username',
  'travel-profile-builder': 'Travel Profile Builder',
  'empty-messages': 'Empty Messages',
  'cancel-request': 'Cancel Request',
  'decline-offer': 'Decline Offer',
  'exit-page': 'Exit Page',
  case: 'Case',
  'general-donation': 'General Donation',
  'saved-credit-cards': 'Saved Credit Cards',
  'upgrade-options-better-view': 'Upgrade Options_ Better view',
  'upgrade-options-larger-suite': 'Upgrade Options_ Larger suite',
  'upgrade-options-no-preference': 'Upgrade Options_ No Preference',
  'upgrade-options-presidential-update': 'Upgrade Options_ Presidential update',
  'travel-profile-banner': 'Travel Profile Banner',
  'app-update-available': 'App Update Available',
} as const

export type IllustrationName = keyof typeof ILLUSTRATION_NAMES

export const Illustration: React.FC<IllustrationProps> = ({ brand, name, width, className = '', alt }) => {
  // Convert brand to lowercase for folder path (fallback when used outside MkSiteProvider, e.g. route tests)
  const brandFolder = (brand ?? configs.mk.defaultThemeBrand).toLowerCase()

  // Get the actual file name from the mapping
  const fileName = ILLUSTRATION_NAMES[name as IllustrationName]

  // If the illustration name is not found in the mapping, throw an error
  if (!fileName) {
    console.error(`Illustration name "${name}" not found in ILLUSTRATION_NAMES mapping. Available names:`, Object.keys(ILLUSTRATION_NAMES))
    return null // Return null instead of trying to load a broken image
  }

  // Construct the image path
  const imagePath = withBase(`/content/images/${brandFolder}/illustrations/${fileName}.svg`)

  // Handle width prop - can be number (px) or string (%, rem, etc.)
  const widthStyle = width ? (typeof width === 'number' ? `${width}px` : width) : undefined

  return (
    <img
      src={imagePath}
      alt={alt || `${name} illustration`}
      className={`illustration ${className}`}
      style={{ width: widthStyle }}
      onError={(e) => {
        // Fallback to a default illustration or show error state
        console.warn(`Failed to load illustration: ${imagePath}`)
        e.currentTarget.style.display = 'none'
      }}
    />
  )
}
